import type { PriceObservation, Product } from '@recipes/contracts';
import {
  cheapestPrice,
  currentPricesByStore,
  priceChange,
  priceTrend,
  unitPriceOf,
} from '@recipes/domain';
import { Badge, Button } from '@recipes/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removePrice } from '@/features/catalog/catalog-queue';
import { useCatalogStores, useShoppingAt } from '@/features/catalog/hooks';
import { storeLabel } from '@/features/catalog/store-label';
import { toastError } from '@/lib/errors';
import {
  formatDay,
  formatMoney,
  today,
  useCurrency,
  usePriceRules,
  usePricesEnabled,
  usePricesFor,
} from './hooks';
import { PriceDialog } from './price-dialog';
import { PriceChangeNote, PriceSparkline } from './price-parts';

/**
 * "Prices" on a product (docs/partial/shopping-in-store-spec.md 5.5, 5.7): the latest price per
 * store, the unit price for comparison, the change against the previous regular price, a small
 * history and the cheapest store. Computed from the device copy – it works without a connection.
 * Prices in another currency stay visible, labelled; they are never converted or compared.
 */
export function ProductPrices({ product }: { product: Product }) {
  const { t } = useTranslation();
  const enabled = usePricesEnabled();
  const observations = usePricesFor({ productId: product.id });
  const stores = useCatalogStores();
  const { store } = useShoppingAt();
  const currency = useCurrency();
  const rules = usePriceRules();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const storeName = useMemo(() => {
    const names = new Map(stores.map((s) => [s.id, storeLabel(s)]));
    return (observation: PriceObservation) =>
      names.get(observation.storeId ?? '') ?? t('prices.dialog.somewhere');
  }, [stores, t]);

  const rows = useMemo(() => {
    const options = { today: today(), staleDays: rules.staleDays };
    return currentPricesByStore(observations, options).map((current) => {
      const place = current.observation.storeId ?? current.observation.chainId;
      const sameStore = observations.filter(
        (o) => (o.storeId ?? o.chainId) === place && o.currency === current.observation.currency,
      );
      return {
        ...current,
        key: `${place ?? 'none'}|${current.observation.currency}`,
        history: sameStore,
        change: priceChange(sameStore, { at: options.today, minPercent: rules.minPercent }),
        trend: priceTrend(sameStore, 365, { at: options.today }),
      };
    });
  }, [observations, rules]);
  const cheapest = useMemo(
    () => cheapestPrice(observations, { today: today(), currency, staleDays: rules.staleDays }),
    [currency, observations, rules.staleDays],
  );

  if (!enabled) return null;

  return (
    <section
      className="space-y-3"
      aria-labelledby="product-prices-heading"
      data-testid="product-prices"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 id="product-prices-heading" className="text-lg font-semibold">
          {t('prices.title')}
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          data-testid="price-add"
        >
          <Plus aria-hidden /> {t('prices.add')}
        </Button>
      </div>

      {rows.length === 0 && <p className="text-sm text-muted-foreground">{t('prices.none')}</p>}

      {cheapest && rows.filter((row) => row.observation.currency === currency).length > 1 && (
        <p className="text-sm" data-testid="price-cheapest">
          {t('prices.cheapest', {
            store: storeName(cheapest.observation),
            price: formatMoney(cheapest.observation.price, currency),
            date: formatDay(cheapest.observation.observedAt),
          })}
        </p>
      )}

      <ul className="divide-y rounded-lg border">
        {rows.map((row) => {
          const { observation } = row;
          const unit = unitPriceOf(observation);
          const foreign = observation.currency !== currency;
          return (
            <li key={row.key} className="space-y-1 p-3" data-testid="price-row">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-sm font-medium">{storeName(observation)}</span>
                <span className="text-sm tabular-nums">
                  {row.stale && <span>{t('prices.approx')} </span>}
                  {formatMoney(observation.price, observation.currency)}
                  {observation.perQuantity &&
                    ` / ${observation.perQuantity} ${observation.perUnitCode}`}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>{formatDay(observation.observedAt)}</span>
                {unit && !observation.perQuantity && (
                  <span>
                    {t('prices.unitPrice', {
                      price: formatMoney(unit.unitPrice, observation.currency),
                      base: t(`prices.base.${unit.unitPriceBase}`),
                    })}
                  </span>
                )}
                {observation.isPromotion && (
                  <Badge variant="secondary">
                    {observation.promotionEndsAt
                      ? t('prices.promotionUntil', { date: formatDay(observation.promotionEndsAt) })
                      : t('prices.promotion')}
                  </Badge>
                )}
                {observation.depositAmount && (
                  <span>
                    {t('prices.deposit', {
                      amount: formatMoney(observation.depositAmount, observation.currency),
                    })}
                  </span>
                )}
                {foreign && <Badge variant="muted">{t('prices.otherCurrency')}</Badge>}
                {store && observation.storeId === store.id && (
                  <Badge variant="muted">{t('prices.here')}</Badge>
                )}
              </div>
              {row.change?.noticeable && <PriceChangeNote change={row.change} />}
              {row.trend && (
                <p className="text-xs text-muted-foreground">
                  {t('prices.trend12', {
                    percent: row.trend.percent.startsWith('-')
                      ? `−${row.trend.percent.slice(1)}`
                      : `+${row.trend.percent}`,
                  })}
                </p>
              )}
              {row.history.length > 1 && (
                <button
                  type="button"
                  className="text-xs underline"
                  aria-expanded={expanded === row.key}
                  onClick={() => setExpanded(expanded === row.key ? null : row.key)}
                >
                  {t('prices.history', { count: row.history.length })}
                </button>
              )}
              {expanded === row.key && (
                <div className="space-y-1 pt-1">
                  <PriceSparkline
                    observations={row.history}
                    label={t('prices.historyChart', { store: storeName(observation) })}
                  />
                  <ul className="text-xs">
                    {[...row.history]
                      .sort((a, b) => b.observedAt.localeCompare(a.observedAt))
                      .map((entry) => (
                        <li key={entry.id} className="flex items-center justify-between gap-2">
                          <span>
                            {formatDay(entry.observedAt)} ·{' '}
                            {formatMoney(entry.price, entry.currency)}
                            {entry.isPromotion && ` · ${t('prices.promotion')}`}
                            {` · ${t(`prices.source.${entry.source}`)}`}
                          </span>
                          {entry.editable && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('prices.delete', {
                                price: formatMoney(entry.price, entry.currency),
                              })}
                              onClick={() => void removePrice(entry).catch(toastError)}
                            >
                              <Trash2 aria-hidden />
                            </Button>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <PriceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        target={{
          productId: product.id,
          name: product.brand ? `${product.brand} ${product.name}` : product.name,
          packageQuantity: product.packageQuantity,
          packageUnitCode: product.packageUnitCode,
        }}
        defaultStoreId={store?.id ?? null}
      />
    </section>
  );
}
